import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { ConfirmationMessage, ErrorMessage } from "@/components/ui/States";
import { BUSINESS } from "@/lib/constants";

export default function AskBeforeYouForget({ defaultCity = "Naples" }) {
    const [phone, setPhone] = useState("");
    const [city, setCity] = useState(defaultCity);
    const [consent, setConsent] = useState(false);
    const [honeypot, setHoneypot] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [error, setError] = useState(null);
    const startMs = useRef(Date.now());

    useEffect(() => { startMs.current = Date.now(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!phone.trim() || !consent) {
            setError("We need your phone and consent to call you back.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post("/leads/micro-form", {
                phone: phone.trim(),
                city: city.trim() || "Naples",
                consent_given: true,
                website_url: honeypot,
                start_ms: startMs.current,
            });
            track(EVENTS.MICRO_FORM_SUBMIT, { lead_id: res.data?.lead_id });
            setSuccess(res.data.confirmation_message);
        } catch (err) {
            setError(err?.response?.data?.detail || "Hmm — that didn't go through. Try again or call us.");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return <ConfirmationMessage message={success} testId="micro-form-success" />;
    }

    return (
        <div className="a1-card a1-bg-alt max-w-md mx-auto" data-testid="ask-before-you-forget">
            <h3 className="text-xl mb-2">Not ready for a full estimate? Just want a quick callback?</h3>
            <p className="text-sm text-[var(--color-text-muted)] mb-4">
                Drop your phone number and we'll call you back during business hours — no form, no commitment.
            </p>
            <form onSubmit={submit} className="grid gap-3">
                <div>
                    <label className="a1-label" htmlFor="abf-phone">Phone Number</label>
                    <input
                        id="abf-phone"
                        type="tel"
                        inputMode="tel"
                        className="a1-input"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="Best number to reach you"
                        autoComplete="tel"
                        required
                        data-testid="abf-phone"
                    />
                </div>
                <div>
                    <label className="a1-label" htmlFor="abf-city">City</label>
                    <input
                        id="abf-city"
                        className="a1-input"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        autoComplete="address-level2"
                        data-testid="abf-city"
                    />
                    <p className="a1-helper">Defaults to Naples. Change if needed.</p>
                </div>
                <label className="flex items-start gap-2 text-sm">
                    <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => setConsent(e.target.checked)}
                        className="mt-1"
                        required
                        data-testid="abf-consent"
                    />
                    <span>I agree to be contacted by A1 Screening about my callback.</span>
                </label>
                <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    className="a1-honeypot"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    name="website_url"
                />
                {error ? <ErrorMessage message={error} testId="abf-error" /> : null}
                <button type="submit" className="a1-btn-secondary w-full" disabled={submitting} data-testid="abf-submit">
                    {submitting ? "Sending…" : "Request a Quick Callback"}
                </button>
                <p className="a1-helper">
                    We only use your number to call you back. Or call us first: <a href={`tel:${BUSINESS.phoneTel}`}>{BUSINESS.phone}</a>.
                </p>
            </form>
        </div>
    );
}
